import AlertRules from '../types/Alerts/AlertRules';
import { AlertRuleFormData, AlertRuleFormValidation } from '../types/Alerts/AlertRuleFormState';

export class AlertRuleFormValidator {
    
    static validateName(
        name: string, 
        currentId: string, 
        alertRules: AlertRules[]
    ): { isValid: boolean; isUsed: boolean; className: string } {
        const trimmedName = name.trim();
        
        if (trimmedName === '') {
            return {
                isValid: false,
                isUsed: false,
                className: 'is-invalid'
            };
        }

        // Check for duplicate names
        const duplicateIndex = alertRules.findIndex(
            rule => parseInt(rule.id) !== Number(currentId) && 
            rule.name.toLowerCase() === trimmedName.toLowerCase()
        );

        if (duplicateIndex !== -1) {
            return {
                isValid: false,
                isUsed: true,
                className: 'is-invalid'
            };
        }

        return {
            isValid: true,
            isUsed: false,
            className: 'is-valid'
        };
    }

    static validateThreshold(
        threshold: number,
        units: string
    ): { isValid: boolean; className: string; message?: string } {
        if (isNaN(threshold) || threshold === null || threshold === undefined) {
            return {
                isValid: false,
                className: 'is-invalid',
                message: 'Please fill in this field'
            };
        }

        if (threshold < 0) {
            return {
                isValid: false,
                className: 'is-invalid',
                message: 'Must be a positive number'
            };
        }

        const isPercent = units === '%';
        if (isPercent && threshold > 100) {
            return {
                isValid: false,
                className: 'is-invalid',
                message: "Can't be more than 100%"
            };
        }

        return {
            isValid: true,
            className: 'is-valid',
            message: undefined
        };
    }

    static validateStatistic(
        statistic: string
    ): { isValid: boolean; hasError: boolean } {
        if (!statistic || statistic.trim() === '') {
            return {
                isValid: false,
                hasError: true
            };
        }

        return {
            isValid: true,
            hasError: false
        };
    }

    static validateCompleteForm(
        formData: AlertRuleFormData,
        alertRules: AlertRules[]
    ): {
        isValid: boolean;
        validation: AlertRuleFormValidation;
    } {
        let isValid = true;

        // Validate name
        const nameValidation = this.validateName(formData.name, formData.id, alertRules);
        if (!nameValidation.isValid) isValid = false;

        // Validate threshold
        const thresholdValidation = this.validateThreshold(formData.threshold, formData.units);
        if (!thresholdValidation.isValid) isValid = false;

        // Validate statistic
        const statisticValidation = this.validateStatistic(formData.statistic);
        if (!statisticValidation.isValid) isValid = false;

        const validation: AlertRuleFormValidation = {
            name: nameValidation,
            threshold: thresholdValidation,
            statistic: statisticValidation,
            isFormValid: isValid
        };

        return {
            isValid,
            validation
        };
    }
}

// Utility functions for business logic
export class AlertRuleFormUtils {
    
    static getEntityOrMetric(key: string, typeValue: string): string {
        const typeArray = typeValue.split('_');
        return typeArray[key === 'entity' ? 0 : 1];
    }

    static buildTypeString(entityType: string, metricType: string): string {
        return `${entityType}_${metricType}`;
    }
}

